/**
 * YouTube bridge utilities for Goblinbox.
 *
 * Provides helpers for parsing, embedding, and working with YouTube URLs
 * within the Nostr video ecosystem.
 */

/**
 * Extracts a YouTube video ID from any known YouTube URL format:
 *   - https://www.youtube.com/watch?v=VIDEO_ID
 *   - https://youtu.be/VIDEO_ID
 *   - https://www.youtube.com/embed/VIDEO_ID
 *   - https://www.youtube.com/shorts/VIDEO_ID
 *   - https://m.youtube.com/watch?v=VIDEO_ID
 *   - https://youtube.com/watch?v=VIDEO_ID&t=123
 *
 * Returns `null` if the URL is not a YouTube URL or the ID cannot be parsed.
 */
export function extractYouTubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.replace(/^(www\.|m\.)/, '');

    if (hostname === 'youtu.be') {
      // https://youtu.be/VIDEO_ID?t=123
      const id = parsed.pathname.slice(1).split('/')[0];
      return isValidYouTubeId(id) ? id : null;
    }

    if (hostname === 'youtube.com') {
      // /watch?v=VIDEO_ID
      if (parsed.pathname === '/watch') {
        const id = parsed.searchParams.get('v');
        return id && isValidYouTubeId(id) ? id : null;
      }

      // /embed/VIDEO_ID or /shorts/VIDEO_ID
      const segments = parsed.pathname.split('/').filter(Boolean);
      if ((segments[0] === 'embed' || segments[0] === 'shorts' || segments[0] === 'v') && segments[1]) {
        const id = segments[1];
        return isValidYouTubeId(id) ? id : null;
      }
    }

    return null;
  } catch {
    return null;
  }
}

/** YouTube video IDs are 11-character alphanumeric strings with dashes and underscores. */
function isValidYouTubeId(id: string): boolean {
  return /^[A-Za-z0-9_-]{11}$/.test(id);
}

/** Returns true if the given URL is any recognised YouTube video URL. */
export function isYouTubeUrl(url: string): boolean {
  return extractYouTubeVideoId(url) !== null;
}

/**
 * Converts a YouTube video ID into a nocookie embed URL.
 * Uses youtube-nocookie.com to avoid third-party cookies.
 */
export function buildYouTubeEmbedUrl(videoId: string, startSeconds?: number): string {
  let url = `https://www.youtube-nocookie.com/embed/${videoId}?modestbranding=1&rel=0`;
  if (startSeconds && startSeconds > 0) {
    url += `&start=${Math.floor(startSeconds)}`;
  }
  return url;
}

/**
 * Returns the best-quality thumbnail URL for a YouTube video.
 * Tries maxresdefault first, falls back to hqdefault.
 */
export function getYouTubeThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

/**
 * Parses a `t=` seconds offset from a YouTube URL (e.g. ?t=123 or &t=1h2m3s).
 */
export function extractYouTubeStartTime(url: string): number | undefined {
  try {
    const parsed = new URL(url);
    const t = parsed.searchParams.get('t');
    if (!t) return undefined;

    // Plain seconds
    if (/^\d+$/.test(t)) return parseInt(t, 10);

    // h/m/s notation: e.g. "1h2m3s", "2m30s", "45s"
    let seconds = 0;
    const hMatch = t.match(/(\d+)h/);
    const mMatch = t.match(/(\d+)m/);
    const sMatch = t.match(/(\d+)s/);
    if (hMatch) seconds += parseInt(hMatch[1], 10) * 3600;
    if (mMatch) seconds += parseInt(mMatch[1], 10) * 60;
    if (sMatch) seconds += parseInt(sMatch[1], 10);
    return seconds > 0 ? seconds : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Strips all tracking and non-essential parameters from a YouTube URL,
 * returning a clean canonical watch URL containing only `v` and, if
 * present, `t` (start time).
 *
 * Removed parameters include (but are not limited to):
 *   - `si`  — YouTube Share Identifier (personalised tracking token)
 *   - `utm_source`, `utm_medium`, `utm_campaign` — UTM analytics
 *   - `feature`, `pp`, `ab_channel` — YouTube internal attribution
 *
 * Examples:
 *   https://youtu.be/dQw4w9WgXcQ?si=abc123
 *     → https://www.youtube.com/watch?v=dQw4w9WgXcQ
 *   https://www.youtube.com/watch?v=dQw4w9WgXcQ&t=42&si=abc123
 *     → https://www.youtube.com/watch?v=dQw4w9WgXcQ&t=42
 */
export function normaliseYouTubeUrl(url: string): string {
  const id = extractYouTubeVideoId(url);
  if (!id) return url;

  // Preserve only the start-time parameter — everything else is stripped.
  const startTime = extractYouTubeStartTime(url);
  const canonical = `https://www.youtube.com/watch?v=${id}`;
  return startTime && startTime > 0 ? `${canonical}&t=${startTime}` : canonical;
}
